import React from 'react';
import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { SPACING, RADIUS, BRAND_BLUE } from '../../theme/theme';

/**
 * Shared loading UI.
 *
 * Two shapes, picked by what the user is waiting for:
 *  - `PageLoader` for an indeterminate wait with no known layout (route
 *    guards, a whole page swapping in).
 *  - The `*Skeleton` components when the shape of the result is known, so the
 *    layout does not jump when data lands.
 *
 * Buttons keep using SubmitButton's own spinner; this module is for regions.
 */

const FONT = 'IRANYekanXFaNum';

export const PageLoader: React.FC<{
  /** Optional caption. Omit for short waits — a bare spinner reads calmer. */
  label?: string;
  minHeight?: number | string;
  size?: number;
}> = ({ label, minHeight = 400, size = 40 }) => (
  <Box
    role="status"
    aria-live="polite"
    aria-busy="true"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: `${SPACING.md}px`,
      minHeight,
      width: '100%',
    }}
  >
    <CircularProgress
      size={size}
      thickness={4}
      sx={{ color: BRAND_BLUE }}
    />
    {label && (
      <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#60646E' }}>
        {label}
      </Typography>
    )}
  </Box>
);

/** Text placeholder: `lines` bars, the last one short like a real paragraph. */
export const TextSkeleton: React.FC<{ lines?: number; width?: string }> = ({
  lines = 3,
  width = '100%',
}) => (
  <Box sx={{ width, display: 'flex', flexDirection: 'column', gap: `${SPACING.xs}px` }}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        variant="text"
        animation="wave"
        width={i === lines - 1 ? '60%' : '100%'}
        height={20}
      />
    ))}
  </Box>
);

/** Stands in for a form: label + field pairs at the real control height. */
export const FormSkeleton: React.FC<{ fields?: number }> = ({ fields = 6 }) => (
  <Box
    role="status"
    aria-busy="true"
    sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: `${SPACING.md}px` }}
  >
    {Array.from({ length: fields }).map((_, i) => (
      <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: `${SPACING.xs}px` }}>
        <Skeleton variant="text" animation="wave" width={110} height={18} />
        <Skeleton
          variant="rectangular"
          animation="wave"
          height={48}
          sx={{ borderRadius: `${RADIUS.md}px` }}
        />
      </Box>
    ))}
  </Box>
);

/** Stands in for a list of cards. */
export const CardListSkeleton: React.FC<{
  count?: number;
  height?: number;
}> = ({ count = 3, height = 120 }) => (
  <Box
    role="status"
    aria-busy="true"
    sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: `${SPACING.md}px` }}
  >
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton
        key={i}
        variant="rectangular"
        animation="wave"
        height={height}
        sx={{ borderRadius: `${RADIUS.md}px` }}
      />
    ))}
  </Box>
);

/** Stands in for a table: a header bar then evenly spaced rows. */
export const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <Box role="status" aria-busy="true" sx={{ width: '100%' }}>
    <Skeleton
      variant="rectangular"
      animation="wave"
      height={40}
      sx={{ borderRadius: `${RADIUS.sm}px`, marginBottom: `${SPACING.sm}px` }}
    />
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton
        key={i}
        variant="rectangular"
        animation="wave"
        height={36}
        sx={{ borderRadius: `${RADIUS.sm}px`, marginBottom: `${SPACING.xs}px` }}
      />
    ))}
  </Box>
);
